import React from 'react';
import { 
  AreaChart, Area, PieChart, Pie, Cell, 
  XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer 
} from 'recharts';

export default function AutonomyDashboard() {
  // Mock Data
  const confidenceData = [
    { name: 'Apr 6', value: 75 },
    { name: 'Apr 9', value: 78 },
    { name: 'Apr 13', value: 74 },
    { name: 'Apr 16', value: 78 },
    { name: 'Apr 20', value: 84 },
    { name: 'Apr 23', value: 79 },
    { name: 'Apr 27', value: 73 },
    { name: 'Apr 30', value: 82 },
    { name: 'May 4', value: 90 },
    { name: 'May 7', value: 92 },
    { name: 'May 11', value: 77 },
    { name: 'May 14', value: 75 },
    { name: 'May 18', value: 89 },
    { name: 'May 21', value: 91 },
    { name: 'May 25', value: 80 },
    { name: 'May 28', value: 78 },
    { name: 'May 31', value: 85 },
    { name: 'Jun 4', value: 92 },
    { name: 'Jun 7', value: 90 },
    { name: 'Jun 10', value: 90 },
  ];

  const recentTraces = [
    { id: 'approve_access_request', workflow: 'User Onboarding', confidence: '98%', outcome: 'Allowed', outcomeColor: '#059669', time: 'May 25, 10:21 AM' },
    { id: 'Data export reques', workflow: 'Beta Governance', confidence: '95%', outcome: 'Monitored', outcomeColor: '#D97706', time: 'May 21, 3:44 AM' },
    { id: 'API one lirtal adjustment', workflow: 'System Optimization', confidence: '85%', outcome: 'Allowed', outcomeColor: '#059669', time: 'May 21, 8:32 AM' },
    { id: 'Vendor risk assessment', workflow: 'Vendor Management', confidence: '78%', outcome: 'Monitored', outcomeColor: '#D97706', time: 'May 26, 4:14 PM' },
    { id: 'Policy exception request', workflow: 'Vendor Management', confidence: '64%', outcome: 'Blocked', outcomeColor: '#DC2626', time: 'May 25, 10:52 AM' },
  ];

  const topPolicies = [
    { name: 'Access Control', count: 124, pct: 100 },
    { name: 'Data Privacy', count: 102, pct: 82 },
    { name: 'Usage Limits', count: 68, pct: 55 },
    { name: 'Vendor Risk', count: 46, pct: 37 },
    { name: 'Compliance', count: 34, pct: 27 },
  ];

  const policyActions = [
    { label: 'Allowed', count: 126, color: '#059669' },
    { label: 'Monitored', count: 35, color: '#D97706' },
    { label: 'Blocked', count: 8, color: '#DC2626' },
    { label: 'Escalated', count: 3, color: '#6B46C1' },
  ];

  // Helper for rendering a small donut pie chart
  const renderDonut = (value, color, centerLabel, subLabel) => {
    const data = [
      { name: 'Active', value: value, color: color },
      { name: 'Inactive', value: 100 - value, color: '#F3F4F6' }
    ];
    return (
      <div style={{ position: 'relative', width: '70px', height: '70px', flexShrink: 0 }}>
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            <Pie data={data} innerRadius={25} outerRadius={32} dataKey="value" startAngle={90} endAngle={-270} stroke="none">
              {data.map((entry, index) => (
                <Cell key={`cell-${index}`} fill={entry.color} />
              ))}
            </Pie>
          </PieChart>
        </ResponsiveContainer>
        <div style={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#111827', lineHeight: 1 }}>{centerLabel}</div>
          {subLabel && <div style={{ fontSize: '9px', color: '#6B7280' }}>{subLabel}</div>}
        </div>
      </div>
    );
  };

  return (
    <div style={{ padding: '24px', backgroundColor: '#F9FAFB', minHeight: '100vh', fontFamily: 'Inter, sans-serif' }}>
      
      {/* Header */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '24px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: 'bold', color: '#111827', margin: 0 }}>Autonomy OS</h1>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 12px', borderRadius: '8px', border: '1px solid #E5E7EB', backgroundColor: '#fff' }}>
          <span style={{ fontSize: '14px', color: '#111827', fontWeight: '500' }}>System Health</span>
          <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#059669' }}></div>
          <span style={{ fontSize: '14px', color: '#059669', fontWeight: '500' }}>Healthy</span>
        </div>
      </div>

      {/* KPI Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: '16px', marginBottom: '20px' }}>
        {/* Card 1: Autonomy Score */}
        <div style={{ backgroundColor: '#fff', padding: '16px', borderRadius: '12px', border: '1px solid #E5E7EB', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <div style={{ fontSize: '14px', color: '#111827', fontWeight: '500', marginBottom: '12px', whiteSpace: 'nowrap' }}>Autonomy Score</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {renderDonut(72, '#4F46E5', '72')}
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: '14px', color: '#4B5563', marginBottom: '2px' }}>/100</div>
              <div style={{ fontSize: '11px', color: '#059669', fontWeight: '500', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>+10 vs. last mo.</div>
            </div>
          </div>
        </div>

        {/* Card 2: Trust Radius */}
        <div style={{ backgroundColor: '#fff', padding: '16px', borderRadius: '12px', border: '1px solid #E5E7EB', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <div style={{ fontSize: '14px', color: '#111827', fontWeight: '500', marginBottom: '12px', whiteSpace: 'nowrap' }}>Trust Radius</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {renderDonut(85, '#059669', '85%')}
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: '15px', color: '#111827', fontWeight: '600', marginBottom: '2px' }}>High</div>
              <div style={{ fontSize: '11px', color: '#059669', fontWeight: '500', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>+5% vs. last mo.</div>
            </div>
          </div>
        </div>

        {/* Card 3: Active Workflows */}
        <div style={{ backgroundColor: '#fff', padding: '16px', borderRadius: '12px', border: '1px solid #E5E7EB', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <div style={{ fontSize: '14px', color: '#111827', fontWeight: '500', marginBottom: '12px', whiteSpace: 'nowrap' }}>Active Workflows</div>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: '28px', fontWeight: 'bold', color: '#111827', lineHeight: 1, marginBottom: '6px' }}>24</div>
              <div style={{ fontSize: '11px', color: '#4B5563', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>+2 workflows</div>
            </div>
            <div style={{ width: '36px', height: '36px', backgroundColor: '#EDE9FE', color: '#6B46C1', borderRadius: '8px', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px', flexShrink: 0 }}>
              ⚡
            </div>
          </div>
        </div>

        {/* Card 4: Decision Confidence */}
        <div style={{ backgroundColor: '#fff', padding: '16px', borderRadius: '12px', border: '1px solid #E5E7EB', display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
          <div style={{ fontSize: '14px', color: '#111827', fontWeight: '500', marginBottom: '12px', whiteSpace: 'nowrap' }}>Decision Confidence</div>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            {renderDonut(92, '#4F46E5', '92%')}
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: '15px', color: '#111827', fontWeight: '600', marginBottom: '2px' }}>+3%</div>
              <div style={{ fontSize: '11px', color: '#4B5563', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>vs. last mo.</div>
            </div>
          </div>
        </div>
      </div>

      {/* Middle Row */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '16px', marginBottom: '20px' }}>
        {/* Chart */}
        <div style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '12px', border: '1px solid #E5E7EB' }}>
          <div style={{ fontSize: '15px', fontWeight: '600', color: '#111827', marginBottom: '20px' }}>Decision Confidence Over Time</div>
          <div style={{ height: '220px' }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={confidenceData}>
                <defs>
                  <linearGradient id="colorConf" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#4F46E5" stopOpacity={0.3}/>
                    <stop offset="95%" stopColor="#4F46E5" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#E5E7EB" />
                <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#6B7280' }} dy={10} minTickGap={30} />
                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#6B7280' }} dx={-10} domain={[0, 100]} ticks={[0, 25, 50, 75, 100]} tickFormatter={(val) => `${val}%`} />
                <Tooltip contentStyle={{ borderRadius: '8px', border: 'none', boxShadow: '0 4px 6px rgba(0,0,0,0.1)' }} />
                <Area type="monotone" dataKey="value" stroke="#4F46E5" strokeWidth={2} fillOpacity={1} fill="url(#colorConf)" activeDot={{ r: 6 }} dot={{ r: 3, fill: '#4F46E5', strokeWidth: 0 }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        {/* Policy Actions */}
        <div style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '12px', border: '1px solid #E5E7EB' }}>
          <div style={{ fontSize: '15px', fontWeight: '600', color: '#111827', marginBottom: '20px' }}>Policy Actions (Last 7 Days)</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '0' }}>
            {policyActions.map((action, idx) => (
              <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '16px 0', borderBottom: idx < policyActions.length - 1 ? '1px solid #F3F4F6' : 'none' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                  <div style={{ width: '16px', height: '16px', borderRadius: '50%', backgroundColor: action.color, opacity: 0.8, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    <div style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#fff' }}></div>
                  </div>
                  <span style={{ fontSize: '14px', color: '#111827' }}>{action.label}</span>
                </div>
                <span style={{ fontSize: '16px', fontWeight: '600', color: '#111827' }}>{action.count}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom Row */}
      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: '16px' }}>
        {/* Recent Decision Traces */}
        <div style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '12px', border: '1px solid #E5E7EB', display: 'flex', flexDirection: 'column' }}>
          <div style={{ fontSize: '15px', fontWeight: '600', color: '#111827', marginBottom: '20px' }}>Recent Decision Traces</div>
          <div style={{ flex: 1 }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left', fontSize: '13px' }}>
              <thead>
                <tr>
                  <th style={{ paddingBottom: '12px', fontWeight: '500', color: '#111827' }}>Decision ID</th>
                  <th style={{ paddingBottom: '12px', fontWeight: '500', color: '#111827' }}>Workflow</th>
                  <th style={{ paddingBottom: '12px', fontWeight: '500', color: '#111827' }}>Confidence</th>
                  <th style={{ paddingBottom: '12px', fontWeight: '500', color: '#111827' }}>Outcome</th>
                  <th style={{ paddingBottom: '12px', fontWeight: '500', color: '#111827' }}>Time</th>
                </tr>
              </thead>
              <tbody>
                {recentTraces.map((trace, index) => (
                  <tr key={index}>
                    <td style={{ padding: '12px 0', color: '#4B5563' }}>{trace.id}</td>
                    <td style={{ padding: '12px 0', color: '#4B5563' }}>{trace.workflow}</td>
                    <td style={{ padding: '12px 0', color: '#111827', fontWeight: '500' }}>{trace.confidence}</td>
                    <td style={{ padding: '12px 0', color: trace.outcomeColor }}>{trace.outcome}</td>
                    <td style={{ padding: '12px 0', color: '#4B5563' }}>{trace.time}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div style={{ marginTop: '16px' }}>
            <button style={{ color: '#4F46E5', background: 'none', border: 'none', fontSize: '14px', cursor: 'pointer', padding: 0 }}>
              View all traces →
            </button>
          </div>
        </div>

        {/* Top Policies */}
        <div style={{ backgroundColor: '#fff', padding: '20px', borderRadius: '12px', border: '1px solid #E5E7EB', display: 'flex', flexDirection: 'column' }}>
          <div style={{ fontSize: '15px', fontWeight: '600', color: '#111827', marginBottom: '20px' }}>Top Policies</div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '16px', flex: 1 }}>
            {topPolicies.map((pol, idx) => (
              <div key={idx} style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '13px' }}>
                <div style={{ width: '100px', color: '#111827' }}>{pol.name}</div>
                <div style={{ flex: 1, margin: '0 16px', height: '6px', backgroundColor: '#F3F4F6', borderRadius: '3px', position: 'relative' }}>
                  <div style={{ position: 'absolute', top: 0, left: 0, height: '100%', width: `${pol.pct}%`, backgroundColor: '#4F46E5', borderRadius: '3px' }}></div>
                </div>
                <div style={{ width: '24px', textAlign: 'right', color: '#111827', fontWeight: '500' }}>{pol.count}</div>
              </div>
            ))}
          </div>
          <div style={{ marginTop: '24px' }}>
            <button style={{ color: '#4F46E5', background: 'none', border: 'none', fontSize: '14px', cursor: 'pointer', padding: 0 }}>
              View all policies →
            </button>
          </div>
        </div>
      </div>

    </div>
  );
}
